import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useApproveMeetingProtocolMutation,
  useDeclineMeetingProtocolMutation,
  useSetMeetingProtocolItemStatusMutation,
  useGetMeetingAssignmentsQuery,
  useRematchMeetingAssignmentsMutation,
  useGetMeetingProtocolQuery,
  useGetMeetingQuery,
  useGetMeetingTranscriptQuery,
  useLazyExportMeetingProtocolDocxQuery,
  useLazyExportMeetingSeriesSummaryDocxQuery,
  useGetMeetingSeriesSummaryQuery,
  useSetMeetingAssignmentStatusMutation,
  useSetMeetingAssignmentDueMutation,
  useDispatchMeetingAssignmentMutation,
  useGetMeetingParticipantsQuery,
  useGetMeetingUserCandidatesQuery,
  useAddMeetingParticipantMutation,
  useRematchMeetingParticipantsMutation,
  useDeleteMeetingParticipantMutation,
  useListMeetingEmployeesQuery,
  useListMeetingDepartmentsQuery,
  useListMeetingSeriesQuery,
  useListPtoProjectsQuery,
  useSetMeetingDepartmentMutation,
  useSetMeetingContextMutation,
  useDeleteMeetingMutation,
  useRestoreMeetingMutation,
  useMeQuery,
} from '@shared/api';
import { useActiveProjectContext } from '@shared/lib/activeProjectContext';
import { useAgentPermission, useMeetingDeleteAccess } from '@entities/user';
import {
  fmtMeetingDate,
  meetingStatusLabel,
  meetingStatusTone,
  type MeetingProtocolItem,
} from '@entities/meetings';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { DeletionBadge, deletionDaysLeft } from './deletion';
import { MeetingProtocolSection } from './MeetingProtocolSection';
import { MeetingAssignmentsSection } from './MeetingAssignmentsSection';
import { MeetingParticipantsSection } from './MeetingParticipantsSection';
import { MeetingSeriesSummarySection, MeetingTranscriptSection } from './MeetingModals';

/** Скачать Blob как файл (клиентский триггер: object URL → клик по ссылке). */
function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

const BackLink = styled(Link)`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const Title = styled.h2`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const Meta = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const StatusTag = styled.span<{ $tone: 'success' | 'danger' | 'neutral' }>`
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(2)}`};
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.text.muted : theme.colors.status[$tone]};
  background: ${({ theme, $tone }) =>
    $tone === 'neutral' ? theme.colors.surfaceHover : `${theme.colors.status[$tone]}18`};
`;

const ActionBtn = styled.button<{ $variant?: 'primary' | 'ghost' }>`
  font-size: 13px;
  font-weight: 600;
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(4)}`};
  border-radius: ${({ theme }) => theme.radius.md};
  cursor: pointer;
  border: 1px solid
    ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.border : theme.colors.primary)};
  background: ${({ theme, $variant }) => ($variant === 'ghost' ? 'transparent' : theme.colors.primary)};
  color: ${({ theme, $variant }) => ($variant === 'ghost' ? theme.colors.text.secondary : '#fff')};

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
  &:hover:not(:disabled) {
    filter: brightness(1.06);
  }
`;

const DeptRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  margin-top: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
`;

const DeptLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const DeptSelect = styled.select`
  font-size: 13px;
  padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(3)}`};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;

  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: ${({ theme }) => theme.shadow.focus};
  }
`;

/**
 * Детальная страница совещания (`/agents/meetings/meetings/:id`). Протокол (просмотр
 * + утверждение/отклонение организатором) и реестр поручений (смена статуса) +
 * скачивание DOCX протокола / контрольного листа серии. Действия утверждения —
 * под правом записи.
 */
export function MeetingDetail() {
  const { id = '' } = useParams();
  const { setActiveProjectId } = useActiveProjectContext();
  const { can_read, can_write } = useAgentPermission('meetings');
  const canDelete = useMeetingDeleteAccess();
  const { data: meData } = useMeQuery();
  const currentUser = meData?.user;
  const { data: meetingData, isError: isMeetingError } = useGetMeetingQuery(id, {
    skip: !id,
    pollingInterval: 5000,
  });

  useEffect(() => {
    if (meetingData?.meeting?.project_id) {
      setActiveProjectId(meetingData.meeting.project_id);
    }
  }, [meetingData?.meeting?.project_id, setActiveProjectId]);

  const { data: protocolData } = useGetMeetingProtocolQuery(id, {
    skip: !id || isMeetingError,
    pollingInterval: 5000,
  });
  const { data: assignmentsData } = useGetMeetingAssignmentsQuery(id, {
    skip: !id || isMeetingError,
  });
  const [approve, { isLoading: approving }] = useApproveMeetingProtocolMutation();
  const [decline, { isLoading: declining }] = useDeclineMeetingProtocolMutation();
  const [setStatus] = useSetMeetingAssignmentStatusMutation();
  const [setDue] = useSetMeetingAssignmentDueMutation();
  const [rematch, { isLoading: rematching }] = useRematchMeetingAssignmentsMutation();
  const [rematchInfo, setRematchInfo] = useState<string | null>(null);
  const [setItemStatus] = useSetMeetingProtocolItemStatusMutation();
  const [exportProtocol, { isFetching: dlProto }] = useLazyExportMeetingProtocolDocxQuery();
  const [exportSeries, { isFetching: dlSeries }] = useLazyExportMeetingSeriesSummaryDocxQuery();
  const [showTranscript, setShowTranscript] = useState(false);
  const { data: transcriptData } = useGetMeetingTranscriptQuery(id, {
    skip: !id || !showTranscript,
  });
  const [showSeries, setShowSeries] = useState(false);
  const { data: seriesSummary } = useGetMeetingSeriesSummaryQuery(
    meetingData?.meeting?.series_id ?? '',
    { skip: !meetingData?.meeting?.series_id || !showSeries },
  );
  const { data: participantsData } = useGetMeetingParticipantsQuery(id, { skip: !id });
  const { data: candData } = useGetMeetingUserCandidatesQuery(undefined, { skip: !can_write });
  const { data: employeesData } = useListMeetingEmployeesQuery(undefined, { skip: !can_write });
  const [addParticipant, { isLoading: adding }] = useAddMeetingParticipantMutation();
  const [delParticipant] = useDeleteMeetingParticipantMutation();
  const [rematchParticipants, { isLoading: rematchingParts }] =
    useRematchMeetingParticipantsMutation();
  const [partsInfo, setPartsInfo] = useState<string | null>(null);
  const { data: deptsData } = useListMeetingDepartmentsQuery(undefined, { skip: !can_write });
  const { data: seriesData } = useListMeetingSeriesQuery(undefined, { skip: !can_write });
  const { data: projectsData } = useListPtoProjectsQuery(undefined, { skip: !can_write });
  const [setMeetingDepartment, { isLoading: settingDept }] = useSetMeetingDepartmentMutation();
  const [setMeetingContext, { isLoading: settingCtx }] = useSetMeetingContextMutation();
  const [dispatchAssignment] = useDispatchMeetingAssignmentMutation();
  const [deleteMeeting, { isLoading: deleting }] = useDeleteMeetingMutation();
  const [restoreMeeting, { isLoading: restoring }] = useRestoreMeetingMutation();

  async function handleRematch() {
    try {
      const r = await rematch(id).unwrap();
      setRematchInfo(
        r.matched > 0
          ? `Сопоставлено: ${r.matched}, уведомлено: ${r.notified}.`
          : 'Новых сопоставлений нет — проверьте, что исполнитель есть в справочнике сотрудников.',
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('rematchAssignments failed', err);
      setRematchInfo('Не удалось выполнить сопоставление.');
    }
  }

  async function handleRematchParticipants() {
    try {
      const r = await rematchParticipants(id).unwrap();
      setPartsInfo(
        r.added > 0
          ? `Добавлено участников: ${r.added}.`
          : 'Новых участников не найдено — проверьте, что они есть в справочнике сотрудников и названы в расшифровке.',
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('rematchParticipants failed', err);
      setPartsInfo('Не удалось пересобрать участников.');
    }
  }

  async function handleDelete() {
    if (!window.confirm('Пометить совещание на удаление? Восстановить можно в течение 30 дней.'))
      return;
    try {
      await deleteMeeting(id).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('deleteMeeting failed', err);
    }
  }

  async function handleRestore() {
    try {
      await restoreMeeting(id).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('restoreMeeting failed', err);
    }
  }

  async function toggleItemControl(item: MeetingProtocolItem) {
    try {
      await setItemStatus({
        itemId: item.id,
        status: item.status === 'active' ? 'superseded' : 'active',
        meetingId: id,
      }).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('setMeetingProtocolItemStatus failed', err);
    }
  }

  async function handleSetDepartment(departmentId: string | null) {
    try {
      await setMeetingDepartment({ meetingId: id, department_id: departmentId }).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('setMeetingDepartment failed', err);
    }
  }

  async function handleSetContext(patch: {
    series_id?: string | null;
    project_id?: string | null;
    contract_id?: string | null;
  }) {
    if (!meeting) return;
    try {
      await setMeetingContext({
        meetingId: id,
        series_id: patch.series_id !== undefined ? patch.series_id : meeting.series_id,
        department_id: meeting.department_id,
        project_id: patch.project_id !== undefined ? patch.project_id : meeting.project_id,
        contract_id: patch.contract_id !== undefined ? patch.contract_id : meeting.contract_id,
      }).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('setMeetingContext failed', err);
    }
  }

  async function handleAddParticipant(payload: {
    name: string;
    vk_chat_id?: string;
    user_id?: string;
  }) {
    try {
      await addParticipant({
        meetingId: id,
        name: payload.name,
        vk_chat_id: payload.vk_chat_id,
        user_id: payload.user_id,
      }).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('addMeetingParticipant failed', err);
    }
  }

  async function handleAddFromDirectory(empId: string) {
    const emp = (employeesData?.employees ?? []).find((e) => e.id === empId);
    if (!emp) return;
    try {
      await addParticipant({
        meetingId: id,
        name: emp.full_name,
        vk_chat_id: emp.vk_chat_id ?? undefined,
        user_id: emp.user_id ?? undefined,
      }).unwrap();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('addMeetingParticipant (directory) failed', err);
    }
  }

  async function downloadProtocol() {
    try {
      triggerDownload(await exportProtocol(id).unwrap(), `protocol_${id}.docx`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('exportMeetingProtocolDocx failed', err);
    }
  }

  async function downloadSeries(sid: string) {
    try {
      triggerDownload(await exportSeries(sid).unwrap(), `series_${sid}.docx`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('exportMeetingSeriesSummaryDocx failed', err);
    }
  }

  if (!can_read) {
    return (
      <FallbackBanner tone="danger" title="Доступ запрещён">
        У вас нет прав на раздел «Совещания».
      </FallbackBanner>
    );
  }

  if (isMeetingError) {
    return (
      <div>
        <BackLink to="/agents/meetings/register">← Перейти в Реестр поручений</BackLink>
        <FallbackBanner tone="warning" title="Доступ к совещанию ограничен">
          Просмотр протокола и расшифровки совещания доступен только организатору и присутствовавшим
          или приглашённым участникам. Поручения со всех совещаний доступны в разделе «Реестр».
        </FallbackBanner>
      </div>
    );
  }

  const meeting = meetingData?.meeting;
  const protocol = protocolData?.protocol;
  const items = protocolData?.items ?? [];
  const assignments = assignmentsData?.assignments ?? [];
  const isDraft = protocol?.status === 'draft' && meeting?.status === 'protocol_draft';
  const seriesId = meeting?.series_id ?? null;
  const segments = transcriptData?.segments ?? [];
  const participants = participantsData?.participants ?? [];
  const candidates = candData?.candidates ?? [];
  const participantNames = new Set(participants.map((p) => p.name));
  const directoryOptions = (employeesData?.employees ?? []).filter(
    (e) => !participantNames.has(e.full_name),
  );
  const depts = deptsData?.departments ?? [];
  const seriesList = seriesData?.series ?? [];
  const projectsList = projectsData?.items ?? [];
  const employeeAutocompleteNames = Array.from(
    new Set([
      ...(employeesData?.employees ?? []).map((e) => e.full_name),
      ...candidates.map((c) => c.full_name),
    ]),
  );

  return (
    <div>
      <datalist id="meeting-employees-datalist">
        {employeeAutocompleteNames.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      <BackLink to="/agents/meetings">← К списку совещаний</BackLink>
      <Header>
        <Title>{meeting?.title ?? 'Совещание'}</Title>
        {meeting && (
          <StatusTag $tone={meetingStatusTone(meeting.status)}>
            {meetingStatusLabel(meeting.status)}
          </StatusTag>
        )}
        {meeting && <Meta>{fmtMeetingDate(meeting.created_at)}</Meta>}
        {meeting?.department_name && <Meta>· отдел: {meeting.department_name}</Meta>}
        {meeting?.deleted_at && (
          <DeletionBadge>на удалении · {deletionDaysLeft(meeting.deleted_at)} дн.</DeletionBadge>
        )}
        {seriesId && (
          <ActionBtn
            $variant="ghost"
            onClick={() => void downloadSeries(seriesId)}
            disabled={dlSeries}
          >
            Контрольный лист серии
          </ActionBtn>
        )}
        {seriesId && (
          <ActionBtn
            $variant="ghost"
            onClick={() => setShowSeries((v) => !v)}
            aria-expanded={showSeries}
          >
            {showSeries ? 'Скрыть сводку серии' : 'Сводка серии'}
          </ActionBtn>
        )}
        {canDelete &&
          meeting &&
          (meeting.deleted_at ? (
            <ActionBtn $variant="ghost" onClick={() => void handleRestore()} disabled={restoring}>
              Восстановить
            </ActionBtn>
          ) : (
            <ActionBtn $variant="ghost" onClick={() => void handleDelete()} disabled={deleting}>
              Удалить
            </ActionBtn>
          ))}
      </Header>

      {can_write && (
        <DeptRow>
          <DeptLabel>Отдел:</DeptLabel>
          <DeptSelect
            value={meeting?.department_id ?? ''}
            onChange={(e) => void handleSetDepartment(e.target.value || null)}
            disabled={settingDept}
            aria-label="Отдел совещания"
          >
            <option value="">— без отдела —</option>
            {depts.map((d) => (
              <option value={d.id} key={d.id}>
                {d.name}
              </option>
            ))}
          </DeptSelect>
          <DeptLabel>Серия:</DeptLabel>
          <DeptSelect
            value={meeting?.series_id ?? ''}
            onChange={(e) => void handleSetContext({ series_id: e.target.value || null })}
            disabled={settingCtx}
            aria-label="Серия совещаний"
          >
            <option value="">— разовое совещание —</option>
            {seriesList.map((s) => (
              <option value={s.id} key={s.id}>
                {s.title}
              </option>
            ))}
          </DeptSelect>
          <DeptLabel>Объект:</DeptLabel>
          <DeptSelect
            value={meeting?.project_id ?? ''}
            onChange={(e) => void handleSetContext({ project_id: e.target.value || null })}
            disabled={settingCtx}
            aria-label="Строительный объект"
          >
            <option value="">— без привязки к объекту —</option>
            {projectsList.map((p) => (
              <option value={p.id} key={p.id}>
                {p.code} — {p.name}
              </option>
            ))}
          </DeptSelect>
        </DeptRow>
      )}

      <MeetingProtocolSection
        meetingId={id}
        protocol={protocol}
        items={items}
        isDraft={isDraft}
        canWrite={can_write}
        onApprove={() => void approve(id)}
        approving={approving}
        onDecline={() => void decline(id)}
        declining={declining}
        onDownloadProtocol={() => void downloadProtocol()}
        downloadingProtocol={dlProto}
        onToggleControl={(item) => void toggleItemControl(item)}
      />

      <MeetingSeriesSummarySection
        seriesId={seriesId ?? ''}
        showSeries={showSeries}
        seriesSummary={seriesSummary}
      />

      <MeetingAssignmentsSection
        meetingId={id}
        assignments={assignments}
        canWrite={can_write}
        isDraft={isDraft}
        currentUser={currentUser}
        onRematch={() => void handleRematch()}
        rematching={rematching}
        rematchInfo={rematchInfo}
        onSetStatus={(assignId, status) =>
          void setStatus({ id: assignId, status, meetingId: id })
        }
        onSetDue={(assignId, dueDate) =>
          void setDue({ id: assignId, due_date: dueDate, meetingId: id })
        }
        onDispatch={(assignId, targetAgent) =>
          void dispatchAssignment({ id: assignId, target_agent: targetAgent, meetingId: id })
        }
      />

      <MeetingParticipantsSection
        meetingId={id}
        participants={participants}
        protocolPresent={!!protocol}
        canWrite={can_write}
        candidates={candidates}
        directoryOptions={directoryOptions}
        onRematchParticipants={() => void handleRematchParticipants()}
        rematchingParts={rematchingParts}
        partsInfo={partsInfo}
        onAddParticipant={(payload) => void handleAddParticipant(payload)}
        onAddFromDirectory={(empId) => void handleAddFromDirectory(empId)}
        adding={adding}
        onDeleteParticipant={(partId) => void delParticipant({ id: partId, meetingId: id })}
      />

      <MeetingTranscriptSection
        showTranscript={showTranscript}
        onToggleTranscript={() => setShowTranscript((v) => !v)}
        segments={segments}
      />
    </div>
  );
}
